import styled, { css } from 'styled-components';

import Loader from '@/components/loader';

const sizes = {
  default: 0.9,
  small: 0.75,
};

/*
 * Legacy button, restyled to the design tokens (pill shape, sentence case).
 * Same props as before. New code should use Button from '@/components/ui'.
 *
 * Defaults to a <div> because many call sites wrap it in a link; it only takes
 * button semantics (role, focus, keyboard) when it has its own onClick.
 * Pass as="button" (and type) for real form controls like submit.
 *
 * Fill and appearance use a doubled class (`&&`): /checkout and /dapp load
 * @bidalihq/ui, whose Tailwind preflight ties a single class on
 * `[type='button|submit|reset']` / `button` with `background-color: initial`.
 * After cart → checkout the page CSS lands last and would hollow out the button.
 */
const Container = styled.div`
  align-items: center;
  border: 1px solid transparent;
  border-radius: 9999px;
  box-sizing: border-box;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-weight: 600;
  justify-content: center;
  letter-spacing: normal;
  line-height: 1;
  margin: 0;
  outline: none;
  padding: 1em 1.5em;
  text-align: center;
  text-decoration: none;
  transition:
    background-color 150ms,
    box-shadow 150ms,
    transform 150ms;
  white-space: nowrap;

  && {
    appearance: none;
  }

  &:hover {
    box-shadow: var(--shadow-md);
    text-decoration: none;
    transform: translateY(-1px);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px color-mix(in oklab, var(--ring) 50%, transparent);
  }

  ${({ $accent, $bordered, disabled, $ghost, $primary, size, theme }) => css`
    && {
      background-color: ${
        disabled
          ? 'var(--muted)'
          : $primary
            ? theme.colors.primary
            : $accent
              ? theme.colors.accent
              : $ghost
                ? 'transparent'
                : 'var(--background)'
      };
    }
    border-color: ${$bordered && !disabled ? theme.colors.primary : 'transparent'};
    color: ${
      disabled
        ? 'var(--muted-foreground)'
        : $primary || $accent
          ? 'var(--primary-foreground)'
          : theme.colors.primary
    };
    font-size: ${sizes[size]}em;

    ${
      disabled &&
      css`
        /* Doubled class: @bidalihq/ui's preflight sets [role='button'] { cursor: pointer }. */
        && {
          cursor: not-allowed;
        }

        &:hover {
          box-shadow: none;
          transform: none;
        }
      `
    }
  `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const Spinner = styled.div`
  height: 1em;
  margin-right: 1em;
  width: 1em;
`;

const Button = ({
  accent,
  as,
  children,
  disabled = false,
  ghost,
  isLoading = false,
  onClick,
  primary,
  size = 'default',
  bordered = true,
  type = 'button',
}) => {
  const isNativeButton = as === 'button';
  const isInteractive = typeof onClick === 'function';

  const handleKeyDown = (event) => {
    if (disabled || event.target !== event.currentTarget) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <Container
      $accent={accent}
      aria-disabled={!isNativeButton && isInteractive && disabled ? 'true' : undefined}
      as={as}
      className="button"
      disabled={disabled}
      $ghost={ghost}
      onClick={!disabled ? onClick : null}
      onKeyDown={!isNativeButton && isInteractive ? handleKeyDown : undefined}
      $primary={primary}
      role={!isNativeButton && isInteractive ? 'button' : undefined}
      size={size}
      $bordered={bordered}
      tabIndex={!isNativeButton && isInteractive && !disabled ? 0 : undefined}
      type={isNativeButton ? type : undefined}
    >
      {isLoading && (
        <Spinner>
          <Loader color="currentColor" mode="inline" size={18} />
        </Spinner>
      )}
      <span>{children}</span>
    </Container>
  );
};

export default Button;
